import 'reflect-metadata';
import { ok as assertOk } from 'assert';
import { v4 } from 'uuid';
import { getUriParts, uriRegex } from './regex';
const jsonClone = (data) => JSON.parse(JSON.stringify(data));
export const communicateUriNamespaceMetadataKey = Symbol('communicateUriNamespaceMetadataKey');
export function uriNamespace(namespace, ...alternatives) {
    assertOk(namespace, `invalid namespace: ${namespace}`);
    return Reflect.metadata(communicateUriNamespaceMetadataKey, [namespace, ...alternatives]);
}
export const getUriNamespaceMetadataFromConstructor = (constructor) => {
    assertOk(Reflect.hasMetadata(communicateUriNamespaceMetadataKey, constructor), `class definition of ${constructor.name} missing @UriNamespace`);
    return jsonClone(Reflect.getMetadata(communicateUriNamespaceMetadataKey, constructor));
};
const generateUri = (constructor, { versioned, uriPart, versionPart, forceNamespace, baseEntityUri }) => {
    let idPart = uriPart;
    let namespace = forceNamespace;
    if (uriPart && baseEntityUri) {
        throw new Error(`can't set uriPart and baseEntityUri`);
    }
    if (baseEntityUri) {
        idPart = Buffer.from(baseEntityUri).toString('hex');
    }
    if (uriPart && uriPart.match(uriRegex)) {
        if (forceNamespace !== namespace) {
            throw new Error(`cannot use forceNamespace (${forceNamespace}) with uriPart with different namespace (${uriPart})`);
        }
        const parts = getUriParts(uriPart);
        idPart = parts.idPart;
        namespace = parts.namespace;
    }
    if (idPart && (idPart.indexOf(':') !== -1 || idPart.indexOf('/') !== -1)) {
        throw new Error('id part cannot contain a colon (:) or forward-slash (/)');
    }
    if (!versioned && versionPart) {
        throw new Error(`versioned false but got versionPart, got: ${versionPart}`);
    }
    if (versioned && versionPart && (versionPart.indexOf(':') !== -1 || versionPart.indexOf('/') !== -1)) {
        throw new Error(`version part cannot contain a colon (:) or forward-slash (/), got: ${versionPart}`);
    }
    const prefixes = getUriNamespaceMetadataFromConstructor(constructor);
    if (namespace && prefixes.indexOf(namespace) === -1) {
        throw new Error(`namespace ${namespace} not allowed for constructor ${constructor.name}, ` +
            `only these namespaces are: ${JSON.stringify(prefixes)}`);
    }
    return `${namespace || prefixes[0]}:${idPart || v4()}${versioned ? `/${versionPart || v4()}` : ''}`;
};
export function entityMixin(Base) {
    const getDefaultNamespace = (constructor) => {
        const namespaces = getUriNamespaceMetadataFromConstructor(constructor);
        if (!namespaces || !namespaces.length || !namespaces[0]) {
            throw new Error(`constructor ${constructor.name} has no @UriNamespace`);
        }
        return namespaces[0];
    };
    return class extends Base {
        getUriNamespace() {
            return getDefaultNamespace(this.constructor);
        }
        generateUri(options) {
            return generateUri(this.constructor, options);
        }
        static getUriNamespace() {
            return getDefaultNamespace(this);
        }
        static generateUri(options) {
            return generateUri(this, options);
        }
        static getDerivedUri(baseEntityUri) {
            if (!baseEntityUri || !baseEntityUri.match(uriRegex)) {
                throw new Error(`bad baseEntityUri ${baseEntityUri}`);
            }
            return generateUri(this, { baseEntityUri });
        }
        static getNewUri(uriPartOrEntityLike) {
            if (uriPartOrEntityLike) {
                if (typeof uriPartOrEntityLike === 'string') {
                    return generateUri(this, { uriPart: uriPartOrEntityLike });
                }
                return generateUri(this, { baseEntityUri: uriPartOrEntityLike.getUri() });
            }
            return generateUri(this, {});
        }
    };
}
//# sourceMappingURL=entity-uri.js.map